import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useWatch } from "react-hook-form";
import shotclassFinishedBg from "../../../../assets/images/Common/shotclassFinishedBg.jpg";
import shotclassFinishedMedal from "../../../../assets/images/Common/shotclassFinishedMedal.png";
import StarRatings from "react-star-ratings";
import Input from "../../../../components/Common/Input/Input";
import classes from "./ShotclassCourseFinished.module.scss";

const ShotclassCourseFinished = ({
  completionTitle = "Congratulations!", 
  completionMessage = "Successfully Finished Course",
  isEditing = false,
  disabled = false,
  completionErrors = null,
  completionRegister = null,
  completionWatch = null,
  completionControl = null
}) => {
  // Local state for character counts to avoid re-renders
  const [titleCharCount, setTitleCharCount] = useState(0);
  const [messageCharCount, setMessageCharCount] = useState(0);

  // Use useWatch for reactive updates without causing parent re-renders (only if control is available)
  const watchedTitle = useWatch({
    control: completionControl,
    name: "completionTitle",
    defaultValue: "",
    disabled: !completionControl
  });

  const watchedMessage = useWatch({
    control: completionControl,
    name: "completionMessage", 
    defaultValue: "",
    disabled: !completionControl
  });

  // Update character counts when watched values change
  useEffect(() => {
    setTitleCharCount(watchedTitle?.length || 0);
  }, [watchedTitle]);

  useEffect(() => {
    setMessageCharCount(watchedMessage?.length || 0);
  }, [watchedMessage]);

  // Initialize character counts from default values
  useEffect(() => {
    if (!watchedTitle && !watchedMessage) {
      setTitleCharCount(completionTitle?.length || 0);
      setMessageCharCount(completionMessage?.length || 0);
    }
  }, [completionTitle, completionMessage, watchedTitle, watchedMessage]);
  return (
    <div className={classes.shotclassCourseFinished} key="completion-screen">
      <img
        src={shotclassFinishedBg}
        alt="shotclass completed"
        className={classes.shotclassCourseFinished__Bg}
        onError={(e) => {
          console.error('Background image failed to load:', e);
          e.target.style.display = 'none';
        }}
        onLoad={() => {
          console.log('Background image loaded successfully');
        }}
      />

      <div className={classes.shotclassCourseFinished__congrats}>
        <img
          src={shotclassFinishedMedal}
          alt="shotclass medal"
          className={classes.shotclassCourseFinished__congrats__medal}
          onError={(e) => {
            console.error('Medal image failed to load:', e);
            e.target.style.display = 'none';
          }}
          onLoad={() => {
            console.log('Medal image loaded successfully');
          }}
        />
        {isEditing ? (
          <>
            <div className={classes.shotclassCourseFinished__congrats__inputField}>
              <Input
                name="completionTitle"
                fieldName="completionTitle"
                type="text"
                placeholder="Congratulations!"
                maxLength={150}
                disabled={disabled}
                error={completionErrors?.completionTitle?.message}
                className={classes.shotclassCourseFinished__congrats__titleInput}
                labelColor="light"
                showLabel={false}
              />
              <div className={classes.shotclassCourseFinished__congrats__charCount}>
                {titleCharCount}/150
              </div>
              {completionErrors?.completionTitle?.message && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  {completionErrors.completionTitle.message}
                </div>
              )}
            </div>
            <div className={classes.shotclassCourseFinished__congrats__inputField}>
              <Input
                name="completionMessage"
                fieldName="completionMessage"
                type="textarea"
                placeholder="Successfully Finished Course"
                maxLength={500}
                disabled={disabled}
                error={completionErrors?.completionMessage?.message}
                className={classes.shotclassCourseFinished__congrats__messageInput}
                labelColor="light"
                showLabel={false}
              />
              <div className={classes.shotclassCourseFinished__congrats__charCount}>
                {messageCharCount}/500
              </div>
              {completionErrors?.completionMessage?.message && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  {completionErrors.completionMessage.message}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={classes.shotclassCourseFinished__congrats__heading}>
              {completionTitle}
            </div>
            <div className={classes.shotclassCourseFinished__congrats__subHeading}>
              {completionMessage}
            </div>
          </>
        )}
      </div>

      <div className={classes.shotclassCourseFinished__ratings}>
        <StarRatings
          rating={5}
          starRatedColor={
            JSON.parse(localStorage.getItem("themeData"))?.CustomData
              ?.PrimaryColor1
          }
          numberOfStars={5}
          name={"rate"}
          starHoverColor={
            JSON.parse(localStorage.getItem("themeData"))?.CustomData
              ?.PrimaryColor1
          }
          starDimension="28px"
          starEmptyColor="rgb(203, 211, 227)"
          starSpacing="2px"
        />
      </div>
    </div>
  );
};

// Custom comparison function for React.memo
const areEqual = (prevProps, nextProps) => {
  // Compare primitive values
  if (
    prevProps.completionTitle !== nextProps.completionTitle ||
    prevProps.completionMessage !== nextProps.completionMessage ||
    prevProps.isEditing !== nextProps.isEditing
  ) {
    return false;
  }

  // For function props, only compare if they are the same reference
  // (useCallback in parent should ensure stability)
  if (
    prevProps.completionWatch !== nextProps.completionWatch ||
    prevProps.completionRegister !== nextProps.completionRegister ||
    prevProps.completionControl !== nextProps.completionControl
  ) {
    return false;
  }

  // Compare error objects by checking specific error messages
  const prevErrors = prevProps.completionErrors;
  const nextErrors = nextProps.completionErrors;
  
  if (
    prevErrors?.completionTitle?.message !== nextErrors?.completionTitle?.message ||
    prevErrors?.completionMessage?.message !== nextErrors?.completionMessage?.message
  ) {
    return false;
  }

  return true;
};

ShotclassCourseFinished.displayName = 'ShotclassCourseFinished';

ShotclassCourseFinished.propTypes = {
  completionTitle: PropTypes.string,
  completionMessage: PropTypes.string,
  isEditing: PropTypes.bool,
  completionErrors: PropTypes.object,
  completionRegister: PropTypes.func,
  completionWatch: PropTypes.func,
  completionControl: PropTypes.object,
};

export default React.memo(ShotclassCourseFinished, areEqual);
